import { Link, NavLink } from "react-router-dom"
import "./index.scss"
import LogoS from "../../assets/images/logo-s.png"
import LogoSubtitle from "../../assets/images/my-name.png"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBars,
  faClose,
  faEnvelope,
  faGears,
  faHome,
  faPersonRunning,
  faSuitcase,
  faUser,
} from "@fortawesome/free-solid-svg-icons"
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons"
import { useCallback, useEffect, useRef, useState } from "react"

const MOBILE_MENU_HISTORY_KEY = "__mobileMenuOpen"

const Sidebar = () => {
  // Navigation bar sur les petites tailles d'écran
  const [showNav, setShowNav] = useState(false)
  const toggleButtonRef = useRef(null)
  const hasMenuHistoryEntryRef = useRef(false)
  const closingFromHistoryRef = useRef(false)
  // false car nous ne voulons que la barre de navigation soit affichée au chargement de la page

  const clearMobileMenuHistoryState = useCallback(() => {
    if (
      hasMenuHistoryEntryRef.current &&
      window.history.state?.[MOBILE_MENU_HISTORY_KEY]
    ) {
      const historyState = window.history.state
      const { [MOBILE_MENU_HISTORY_KEY]: _ignored, ...nextHistoryState } =
        historyState

      window.history.replaceState(nextHistoryState, "", window.location.href)
    }

    hasMenuHistoryEntryRef.current = false
    closingFromHistoryRef.current = false
  }, [])

  const closeMobileMenu = useCallback(() => {
    if (!showNav) {
      return
    }

    if (
      hasMenuHistoryEntryRef.current &&
      window.history.state?.[MOBILE_MENU_HISTORY_KEY]
    ) {
      closingFromHistoryRef.current = true
      window.history.back()
      return
    }

    clearMobileMenuHistoryState()
    setShowNav(false)
  }, [showNav, clearMobileMenuHistoryState])

  const closeMobileMenuForNavigation = useCallback(() => {
    if (!showNav) {
      return
    }

    clearMobileMenuHistoryState()
    setShowNav(false)
  }, [showNav, clearMobileMenuHistoryState])

  useEffect(() => {
    const bodyClassName = "mobile-menu-open"

    if (showNav) {
      document.body.classList.add(bodyClassName)
    } else {
      document.body.classList.remove(bodyClassName)
    }

    return () => {
      document.body.classList.remove(bodyClassName)
    }
  }, [showNav])

  useEffect(() => {
    if (!showNav || hasMenuHistoryEntryRef.current) {
      return
    }

    const currentHistoryState =
      window.history.state && typeof window.history.state === "object"
        ? window.history.state
        : {}

    window.history.pushState(
      {
        ...currentHistoryState,
        [MOBILE_MENU_HISTORY_KEY]: true,
      },
      "",
      window.location.href
    )

    hasMenuHistoryEntryRef.current = true
  }, [showNav])

  useEffect(() => {
    const handlePopState = () => {
      if (closingFromHistoryRef.current) {
        closingFromHistoryRef.current = false
        hasMenuHistoryEntryRef.current = false
        setShowNav(false)
        return
      }

      if (hasMenuHistoryEntryRef.current) {
        hasMenuHistoryEntryRef.current = false
        setShowNav(false)
      }
    }

    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  }, [])

  useEffect(() => {
    if (!showNav) {
      return undefined
    }

    const handleEscapeKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMobileMenu()
        toggleButtonRef.current?.focus()
      }
    }

    document.addEventListener("keydown", handleEscapeKeyDown)

    return () => {
      document.removeEventListener("keydown", handleEscapeKeyDown)
    }
  }, [showNav, closeMobileMenu])

  return (
    <div className="nav-bar">
      <Link className="logo" to="/">
        <img src={LogoS} alt="logo" />
        <img className="sub-logo" src={LogoSubtitle} alt="Souleymane" />
      </Link>
      {/* 
                A présent, nous utilisons une classe pour l'affichage de la barre
                de navigation sur les petits écrans
            */}
      {showNav && (
        <button
          type="button"
          className="mobile-menu-backdrop"
          aria-label="Fermer le menu"
          onClick={closeMobileMenu}
        />
      )}

      <nav id="sidebar-mobile-nav" className={showNav ? "mobile-show" : ""}>
        <NavLink
          exact="true"
          activeclassname="active"
          to="/"
          // Nous fermons la barre de navigation
          onClick={closeMobileMenuForNavigation}
        >
          <FontAwesomeIcon icon={faHome} color="#4d4d4e" />
        </NavLink>
        <NavLink
          exact="true"
          activeclassname="active"
          className="about-link"
          to="/about"
          onClick={closeMobileMenuForNavigation}
        >
          <FontAwesomeIcon icon={faUser} color="#4d4d4e" />
        </NavLink>
        <NavLink
          exact="true"
          activeclassname="active"
          className="portfolio-link"
          to="/portfolio"
          onClick={closeMobileMenuForNavigation}
        >
          <FontAwesomeIcon icon={faSuitcase} color="#4d4d4e" />
        </NavLink>
        <NavLink
          exact="true"
          activeclassname="active"
          className="services-link"
          to="/services"
          onClick={closeMobileMenuForNavigation}
        >
          <FontAwesomeIcon icon={faGears} color="#4d4d4e" />
        </NavLink>
        <NavLink
          exact="true"
          activeclassname="active"
          className="contact-link"
          to="/contact"
          onClick={closeMobileMenuForNavigation}
        >
          <FontAwesomeIcon icon={faEnvelope} color="#4d4d4e" />
        </NavLink>
        <NavLink
          exact="true"
          activeclassname="active"
          className="activities-link"
          to="/activities"
          onClick={closeMobileMenuForNavigation}
        >
          <FontAwesomeIcon icon={faPersonRunning} color="#4d4d4e" />
        </NavLink>
      </nav>

      <ul>
        <li>
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.linkedin.com/in/sdley"
          >
            <FontAwesomeIcon icon={faLinkedin} color="#4d4d4e" />
          </a>
        </li>
        <li>
          <a target="_blank" rel="noreferrer" href="https://github.com/sdley">
            <FontAwesomeIcon icon={faGithub} color="#4d4d4e" />
          </a>
        </li>
      </ul>
      {/* Gestion de la responsivité : bouton toggle du menu mobile */}
      <button
        type="button"
        className="menu-toggle-btn"
        ref={toggleButtonRef}
        aria-controls="sidebar-mobile-nav"
        aria-expanded={showNav}
        aria-label={showNav ? "Fermer le menu" : "Ouvrir le menu"}
        onClick={() => {
          if (showNav) {
            closeMobileMenu()
            return
          }

          setShowNav(true)
        }}
      >
        <FontAwesomeIcon
          icon={showNav ? faClose : faBars}
          color="#ffd700"
          size="2x"
        />
      </button>
    </div>
  )
}

export default Sidebar
